import { FC } from 'react';
import { Form, Formik, FormikHelpers } from 'formik';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import { Check, Percent } from 'lucide-react';
import { branchesAPI } from 'services/swagger-api';
import { Button } from 'components/ui/Button';
import { FormikSwitch } from 'components/forms/formik/FormikSwitch';
import { FormikInputWithUnit } from 'components/forms/formik/FormikInputWithUnit';
import { BlockCard } from 'back-office/shared/components/BlockCard';
import {
  applyValidationErrorsToForm,
  parseApiError,
} from 'shared/utils/apiValidationErrors';
import { DERIVED_ROLE_NAMES, DERIVED_ROLES } from './guaranteeLabels';
import { Branch } from './useBranches';

interface DerivedPremiumsFormValues {
  derivedPremiumsEnabled: boolean;
  /** Nombre, ou texte décimal en cours de saisie (« 0. »). */
  catNatRate: number | string;
  catTechRate: number | string;
  attackRate: number | string;
}

const RATE_FIELDS: { name: keyof DerivedPremiumsFormValues; label: string }[] = [
  { name: 'catNatRate', label: 'Taux Cat Nat' },
  { name: 'catTechRate', label: 'Taux Cat Tech' },
  { name: 'attackRate', label: 'Taux Attentat' },
];

/** Un taux n'est exigé que si les primes dérivées sont activées. */
const rateSchema = Yup.number()
  .typeError('Taux invalide')
  .min(0, 'Le taux est compris entre 0 et 100')
  .max(100, 'Le taux est compris entre 0 et 100')
  .when('derivedPremiumsEnabled', {
    is: true,
    then: (schema) => schema.required('Le taux est requis'),
  });

const validationSchema = Yup.object().shape({
  catNatRate: rateSchema,
  catTechRate: rateSchema,
  attackRate: rateSchema,
});

const toRate = (value: number | string): number => {
  const rate = Number(value);
  return Number.isFinite(rate) ? rate : 0;
};

/** Les erreurs de validation du back nomment les champs C# (`tauxCatNat`…). */
const resolveField = (member: string): string | undefined => {
  const m = member.toLowerCase();
  if (m.includes('tauxcatnat') || m.includes('catnatrate')) return 'catNatRate';
  if (m.includes('tauxcattech') || m.includes('cattechrate')) return 'catTechRate';
  if (m.includes('tauxattentat') || m.includes('attackrate')) return 'attackRate';
  return undefined;
};

interface BranchDerivedPremiumsCardProps {
  branch: Branch;
  /** Recharge la branche et ses garanties après l'enregistrement. */
  onSaved: () => void | Promise<void>;
}

/**
 * Primes dérivées (Cat Nat, Cat Tech, Attentat) d'une branche : interrupteur et taux.
 *
 * Activées, les trois primes sont calculées sur une même assiette, la somme des primes
 * pures des garanties « dans l'assiette ». Désactivées, le rôle des garanties est ignoré
 * et n'apparaît nulle part.
 */
export const BranchDerivedPremiumsCard: FC<BranchDerivedPremiumsCardProps> = ({
  branch,
  onSaved,
}) => {
  const initialValues: DerivedPremiumsFormValues = {
    derivedPremiumsEnabled: branch.derivedPremiumsEnabled,
    catNatRate: branch.catNatRate,
    catTechRate: branch.catTechRate,
    attackRate: branch.attackRate,
  };

  const handleSubmit = async (
    values: DerivedPremiumsFormValues,
    { setFieldError, setFieldTouched }: FormikHelpers<DerivedPremiumsFormValues>
  ) => {
    try {
      const response = await branchesAPI.updateBranchDerivedPremiums({
        brancheId: branch.id,
        updatePrimesDeriveesBrancheCommand: {
          derivedPremiumsEnabled: values.derivedPremiumsEnabled,
          catNatRate: toRate(values.catNatRate),
          catTechRate: toRate(values.catTechRate),
          attackRate: toRate(values.attackRate),
        },
      });

      if (response.isSuccess) {
        toast.success('Primes dérivées enregistrées');
        await onSaved();
      } else {
        toast.error(response.errorMessage || "L'enregistrement a échoué");
      }
    } catch (error) {
      console.error('Enregistrement des primes dérivées impossible', error);
      const { message, validationErrors } = await parseApiError(error);
      const matched = applyValidationErrorsToForm({
        validationErrors,
        setFieldError,
        setFieldTouched,
        resolveField,
      });
      if (matched === 0) {
        toast.error(message || "L'enregistrement a échoué");
      }
    }
  };

  const derivedGuaranteeOf = (role: string) =>
    branch.derivedGuarantees.find((guarantee) => guarantee.role === role);

  return (
    <BlockCard
      title="Primes dérivées (Cat Nat, Cat Tech, Attentat)"
      description="Calculées sur l'assiette des garanties de la branche"
      icon={<Percent className="h-4 w-4" />}
    >
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({ values, dirty, isSubmitting }) => (
          <Form className="flex flex-col gap-4">
            <div className="flex flex-col gap-1">
              <FormikSwitch name="derivedPremiumsEnabled" label="Activé" falseLabel="Désactivé" />
              <p className="text-xs text-gray-500">
                {values.derivedPremiumsEnabled
                  ? 'Chaque garantie de la branche reçoit un rôle : dans l’assiette, garantie dérivée ou hors calcul. La prime d’une garantie dérivée vaut son taux appliqué à l’assiette, sa formule de tunnel est ignorée.'
                  : 'Aucune prime dérivée n’est calculée sur cette branche : le rôle des garanties est ignoré et n’apparaît pas sur leur fiche, leurs formules de tunnel s’appliquent.'}
              </p>
            </div>

            {values.derivedPremiumsEnabled && (
              <>
                <div className="grid grid-cols-1 gap-4 border-t border-gray-100 pt-4 sm:grid-cols-3">
                  {RATE_FIELDS.map((field) => (
                    <FormikInputWithUnit
                      key={field.name}
                      id={field.name}
                      name={field.name}
                      label={field.label}
                      unit="%"
                      groupThousands
                    />
                  ))}
                </div>

                <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                  {DERIVED_ROLES.map((role) => {
                    const guarantee = derivedGuaranteeOf(role);
                    return (
                      <div
                        key={role}
                        className="flex items-center justify-between gap-2 rounded-lg bg-orange-50 px-3 py-2 text-xs"
                      >
                        <span className="text-zinc-700">Garantie {DERIVED_ROLE_NAMES[role]}</span>
                        <span
                          className={
                            guarantee
                              ? 'font-mono font-semibold text-orange-700'
                              : 'italic text-gray-400'
                          }
                          title={guarantee?.shortLabel ?? undefined}
                        >
                          {guarantee?.code || 'non affectée'}
                        </span>
                      </div>
                    );
                  })}
                </div>
                <p className="text-xs text-gray-500">
                  Le rôle se choisit sur la fiche de chaque garantie, au plus une garantie par
                  rôle dérivé. Un rôle sans garantie affectée ne donne aucune prime.
                </p>
              </>
            )}

            <div className="flex justify-end">
              <Button color="primary" type="submit" disabled={!dirty || isSubmitting}>
                <Check data-slot="icon" />
                Enregistrer
              </Button>
            </div>
          </Form>
        )}
      </Formik>
    </BlockCard>
  );
};
